import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Tabs,
  Tab,
  Grid,
  Paper,
  IconButton,
  FormControlLabel,
  Checkbox,
  Container,
  RadioGroup,
  Radio,
  FormHelperText,
  Slider,
} from '@mui/material';
import { ArrowBack, KeyboardArrowUp } from '@mui/icons-material';
import { ChannelkartNetworkGet } from '../../services/networkService';
import { ChannelkartNetworkPost } from '../../services/networkService';
import { v4 as uuidv4 } from 'uuid';
import { openPopup } from '../../stateManagement/actions/popupActions';
import { getAccessToken, getLob } from '../../services/authenticationService';
import { fileAttributes } from '../../components/genericFileUpload/genericFileUploadTypes';
import GenericFileUpload from '../../components/genericFileUpload/GenericFileUpload';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

interface WidgetConfig {
  mappedApiField: boolean;
  label: string;
  value?: string;
  editable?: boolean;
  hintText?: string;
  required?: boolean;
  inputType?: { type: string; values: string[]; selected: string };
  list?: string[];
  defaultOption?: string;
  maxCharacters?: number;
  difference?: string;
  options?: { label: string; value: string }[];
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  maxOptions?: number;
  display?:string;
  gallery?: boolean;
  camera?: boolean;
  regexValidation?: string;
}

interface Widget {
  id: string;
  config: WidgetConfig;
  sectionId: string;
  columnName: string;
  widgetType: 'text-input' | 'dropdown' | 'checkbox' | 'textarea' | 'date-picker' | 'radio-group' | 'ranged-date' | 'slider' | 'multiselect-dropdown' | 'camera' | 'location';
  mappedApiField: string;
  label: string;
}

interface FormSection {
  id: string;
  name: string;
  widgets: Widget[];
}

interface FormConfig {
  api: string;
  formId: string;
  formName: string;
  sections: FormSection[];
  buttonLabel: string;
}

interface DynamicFormPageProps {
  setRefresh: (value: boolean) => void;
  mdmState:any
  formConfig: FormConfig;
  initialData: Record<string, any> | null;
  onBack?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export const DynamicFormPage: React.FC<DynamicFormPageProps> = ({
  setRefresh,
  mdmState,
  formConfig,
  initialData,
  onBack
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<{ [key: string]: string[] }>({});
  const [fieldNameToColumnNameMap, setFieldNameToColumnNameMap] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isNewRecord, setIsNewRecord] = useState(false);           
  const selectedCard = mdmState?.name;

  useEffect(() => {
    
  
    // Check if this is a new record (no initialData or empty initialData)
    const isNew = !initialData || Object.keys(initialData).length === 0;
    setIsNewRecord(isNew);
  
    const initialFormData: Record<string, any> = {};
    const mapping: Record<string, string> = {};

    mdmState?.tableConfiguration?.columns?.forEach((col: any) => {
      mapping[col.id] = col.postMappingField;
    });
  
    formConfig?.sections?.forEach(section => {
      section.widgets.forEach(widget => {
        const fieldName = widget.mappedApiField;
  
        initialFormData[fieldName] =
          initialData?.[fieldName] ??
          // initialData?.extendedAttributes?.[fieldName] ??
          // initialData?.extendedAttributes?.['shipTo']?.[fieldName] ??
          '';
  
      });
    });
  
  
    setFormData(initialFormData);
    setFieldNameToColumnNameMap(mapping);
  }, [formConfig, initialData]);


  useEffect(() => {
    async function fetchMappedOptions() {
      try {
        const response = await ChannelkartNetworkGet(
          "/metadata/clientconfig?name=data_mapping_configuration"
        );
  
        if (response.data?.features?.[0]?.domainValues) {
          const mappedOptions = response.data.features[0].domainValues;
          
          const selectedCardConfig = mappedOptions[0][selectedCard]; 
          const transformedOptions: { [key: string]: string[] } = {};
  
          if (selectedCardConfig?.configs && Array.isArray(selectedCardConfig.configs)) {
            for (const config of selectedCardConfig.configs) {
              let options: string[] = [];

              const correspondingWidget = formConfig?.sections
                ?.flatMap(section => section.widgets)
                ?.find(widget => widget.mappedApiField === config.id);
              const base = 'https://datastream.salescode.ai/table-generic/filter-values';
              const url = `${base}?report=${config.report}&which=${config.apiKey}`;
              if (config.api) {
                try {
                  const token = getAccessToken();
                  const tenantId = getLob();
                  const response = await fetch(config.api, {
                    headers: {
                    'authorization': `Bearer ${token}`,
                    'x-tenant-id': tenantId,
                  }
                });
                const json = await response.json();
                const list: string[] = Array.isArray(json?.values?.[config.apiKey || ''])
                    ? json.values[config.apiKey || '']
                    : [];
                const data = list;
                  if (Array.isArray(data) && data.length > 0) {
                    if (options.length === 0) {
                      options = Array.from(new Set(data));
                    }
                  } else {
                    options = Array.isArray(config.options) ? config.options : [];
                  }
                } catch (err) {
                  console.error(`Error fetching data for ${config.id}`, err);
                  options = Array.isArray(config.options) ? config.options : [];
                }
              } else {
                options = Array.isArray(config.options) ? config.options : [];
              }
              const optionKey = correspondingWidget ? correspondingWidget.mappedApiField : config.id;
              transformedOptions[optionKey] = options;
            }
          }
          setOptions(transformedOptions);
        } else {
          setOptions({});
        }
      } catch (error) {
        console.error("Error fetching mapped options:", error);
      }
    }
  
    fetchMappedOptions();
  }, []);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
  
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
    const telRegex = /^\+?[\d\s-]{7,}$/;
    const numberRegex = /^-?\d*\.?\d+$/;
  
    formConfig?.sections?.forEach((section) => {
      section.widgets.forEach((widget) => {
        const fieldName = widget.mappedApiField;
        if (!fieldName) return; // skip widgets without mappedApiField
        const value = formData[fieldName];
        const isRequired = widget.config.required;
  
        if (isRequired) {
          if (widget.widgetType === 'text-input' || widget.widgetType === 'textarea' || widget.widgetType === 'date-picker') {
            if (!value || (typeof value === 'string' && value.trim() === '')) {
              newErrors[fieldName] = `${widget.config.label || fieldName} is required`;
              isValid = false;
            }
          } else if (widget.widgetType === 'dropdown' || widget.widgetType === 'radio-group') {
            if (!value) {
              newErrors[fieldName] = `Please select an option for ${widget.config.label || fieldName}`;
              isValid = false;
            }
          } else if (widget.widgetType === 'multiselect-dropdown' || widget.widgetType === 'checkbox') {
            if (!Array.isArray(value) || value.length === 0) {
              newErrors[fieldName] = `At least one option must be selected for ${widget.config.label || fieldName}`;
              isValid = false;
            }
          } else if (widget.widgetType === 'ranged-date') {
            if (!value?.startDate || !value?.endDate) {
              newErrors[fieldName] = `Both start and end dates are required for ${widget.config.label || fieldName}`;
              isValid = false;
            }
          } else if (widget.widgetType === 'location') {
            if (!value?.location || !value?.lat || !value?.lng) {
              newErrors[fieldName] = `Location, latitude, and longitude are required for ${widget.config.label || fieldName}`;
              isValid = false;
            }
          } else if (widget.widgetType === 'camera') {
            if (!value?.url || (typeof value.url === 'string' && value.url.trim() === '')) {
              newErrors[fieldName] = `Image upload is required for ${widget.config.label || fieldName}`;
              isValid = false;
            }
          } else if (widget.widgetType === 'slider') {
            if (!value && value !== 0) {
              newErrors[fieldName] = `Value is required for ${widget.config.label || fieldName}`;
              isValid = false;
            }
          }
        }

        if ((widget.widgetType === 'text-input' || widget.widgetType === 'textarea') && widget.config.maxCharacters) {
          if (value && typeof value === 'string' && value.length > widget.config.maxCharacters) {
            newErrors[fieldName] = `Input exceeds maximum length of ${widget.config.maxCharacters} characters`;
            isValid = false;
          }
        }
        
        if (widget.widgetType === 'text-input' && value && typeof value === 'string' && widget.config.regexValidation) {
          try {
            const userRegex = new RegExp(widget.config.regexValidation);
            if (!userRegex.test(value)) {
              newErrors[fieldName] = `Input does not match the required pattern for ${widget.config.label || fieldName}`;
              isValid = false;
            }
          } catch (e) {
            console.error(`Invalid regex pattern in config for ${fieldName}: ${widget.config.regexValidation}`, e);
            newErrors[fieldName] = `Invalid regex pattern configured for ${widget.config.label || fieldName}`;
            isValid = false;
          }
        }
        else if (widget.widgetType === 'text-input' && value && typeof value === 'string' && widget.config.inputType?.selected && !widget.config.regexValidation) {
          const inputType = widget.config.inputType.selected;
          if (inputType === 'email' && !emailRegex.test(value)) {
            newErrors[fieldName] = `Please enter a valid email address for ${widget.config.label || fieldName}`;
            isValid = false;
          } else if (inputType === 'url' && !urlRegex.test(value)) {
            newErrors[fieldName] = `Please enter a valid URL for ${widget.config.label || fieldName}`;
            isValid = false;
          } else if (inputType === 'tel' && !telRegex.test(value)) {
            newErrors[fieldName] = `Please enter a valid phone number for ${widget.config.label || fieldName}`;
            isValid = false;
          } else if (inputType === 'number' && !numberRegex.test(value)) {
            newErrors[fieldName] = `Please enter a valid number for ${widget.config.label || fieldName}`;
            isValid = false;
          }
        }

        if (widget.widgetType === 'multiselect-dropdown' && Array.isArray(value) && widget.config.maxOptions) {
          if (value.length > widget.config.maxOptions) {
            newErrors[fieldName] = `You can select up to ${widget.config.maxOptions} options for ${widget.config.label || fieldName}`;
            isValid = false;
          }
        }

        if (widget.widgetType === 'slider' && value !== undefined && value !== null) {
          const numValue = Number(value);
          if (widget.config.min !== undefined && numValue < Number(widget.config.min)) {
            newErrors[fieldName] = `Value must be at least ${widget.config.min} for ${widget.config.label || fieldName}`;
            isValid = false;
          }
          if (widget.config.max !== undefined && numValue > Number(widget.config.max)) {
            newErrors[fieldName] = `Value must not exceed ${widget.config.max} for ${widget.config.label || fieldName}`;
            isValid = false;
          }
        }
      });
    });
  
    setErrors(newErrors);
    return isValid;
  };

const handleSave = async () => {
  
  if (!validateForm()) {
    openPopup('Error', 'Please fill all required fields and correct any errors');
    return;
  }

  try {
    const lob = getLob();
    const transformedData: Record<string, any> = {};
    const formFieldMap: Record<string, string> = {};
    Object.entries(formData).forEach(([id, value]) => {
      const columnName = fieldNameToColumnNameMap[id] || id;
      formFieldMap[id] = columnName;
      transformedData[columnName] = value;
    });

    // const allColumns: any[] = mdmState?.tableConfiguration?.columns || [];

    // allColumns.forEach((col) => {
    //   const { id, columnName } = col;
    //   if (!(columnName in transformedData)) {
    //     transformedData[columnName] =
    //       initialData?.[id] ?? initialData?.extendedAttributes?.[id] ?? null;
    //   }
    // });

    

    const requestBody = {
      requestId: uuidv4(),
      groupId: new Date().toISOString().split('T')[0],
      lob: lob,
      transformerInfo: mdmState.transformer,
      topicName: mdmState.topicName,
      preserveOnFailure: mdmState.preserveOnFailure,
      features: [transformedData],
    };

    const response = await ChannelkartNetworkPost(`/${mdmState.postAPI}`, requestBody);

    const features = response?.data?.features || response?.features;
    const firstFeature = Array.isArray(features) && features.length > 0 ? features[0] : null;

    if (firstFeature && firstFeature.status === "Success") {
      openPopup('Success', 'Record updated successfully!');
      setFormData(formData);
      setRefresh(true);
    } else if (firstFeature && firstFeature.error) {
      openPopup('Error', 'Something went wrong. Please try again.');
    } else {
      openPopup('Error', 'Something went wrong. Please try again.');
    }
  
    
  } catch (error) {
    console.error('Error saving data:', error);
    openPopup('Error', 'Failed to save data. Please try again.');
  }
};

const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
  const value = event.target.value;

  
  const widget = formConfig?.sections
    .flatMap((section) => section.widgets)
    .find((w) => w.mappedApiField === field);
  
  // Check max characters validation
  if (widget?.config.maxCharacters && value.length > widget.config.maxCharacters) {
    setErrors((prev) => ({
      ...prev,
      [field]: `Input cannot exceed ${widget.config.maxCharacters} characters`,
    }));
    return;
  }

  if (widget?.widgetType === 'text-input' && value && typeof value === 'string' && widget.config.regexValidation) {
    try {
      const userRegex = new RegExp(widget.config.regexValidation);
      if (!userRegex.test(value)) {
        setErrors((prev) => ({
          ...prev,
          [field]: `Input does not match the required pattern for ${widget.config.label || field}`,
        }));
        setFormData((prev) => ({
          ...prev,
          [field]: value,
        }));
        return;
      }
    } catch (e) {
      console.error(`Invalid regex pattern in config for ${field}: ${widget.config.regexValidation}`, e);
      setErrors((prev) => ({
        ...prev,
        [field]: `Invalid regex pattern configured for ${widget.config.label || field}`,
      }));
      return;
    }
  }

  else if (widget?.widgetType === 'text-input' && value && typeof value === 'string' && widget.config.inputType?.selected && !widget.config.regexValidation) {
    const inputType = widget.config.inputType.selected;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
    const telRegex = /^\+?[\d\s-]{7,}$/;
    const numberRegex = /^-?\d*\.?\d+$/;

    let inputTypeError = '';
    if (inputType === 'email' && !emailRegex.test(value)) {
      inputTypeError = `Please enter a valid email address for ${widget.config.label || field}`;
    } else if (inputType === 'url' && !urlRegex.test(value)) {
      inputTypeError = `Please enter a valid URL for ${widget.config.label || field}`;
    } else if (inputType === 'tel' && !telRegex.test(value)) {
      inputTypeError = `Please enter a valid phone number for ${widget.config.label || field}`;
    } else if (inputType === 'number' && !numberRegex.test(value)) {
      inputTypeError = `Please enter a valid number for ${widget.config.label || field}`;
    }

    if (inputTypeError) {
      setErrors((prev) => ({
        ...prev,
        [field]: inputTypeError,
      }));
      setFormData((prev) => ({
        ...prev,
        [field]: widget.config.inputType?.selected === 'number' && value !== '' && !isNaN(Number(value)) ? Number(value) : value,
      }));
      return;
    }
  }

  setErrors((prev) => ({ ...prev, [field]: '' })); 
  setFormData((prev) => ({
    ...prev,
    [field]: widget?.config.inputType?.selected === 'number' && value !== '' && !isNaN(Number(value)) ? Number(value) : value,
  }));
};

const handleSelectChange = (field: string) => (event: any) => {
  const widget = formConfig?.sections
    .flatMap((section) => section.widgets)
    .find((w) => w.mappedApiField === field);

  if (widget?.widgetType === 'multiselect-dropdown') {
    const selectedValues = event.target.value as string[];
    if (widget.config.maxOptions && selectedValues.length > widget.config.maxOptions) {
      setErrors((prev) => ({
        ...prev,
        [field]: `You can select up to ${widget.config.maxOptions} options for ${widget.config.label || field}`,
      }));
      return;
    }
  }

  setErrors((prev) => ({ ...prev, [field]: '' }));
  setFormData((prev) => ({
    ...prev,
    [field]: event.target.value,
  }));
};

const handleDateChange = (field: string) => (value: Dayjs | null) => {
  const formattedDate = value?.format('YYYY-MM-DD') || '';
  
  
  setErrors((prev) => ({ ...prev, [field]: '' }));
  setFormData((prev) => {
    const updated = {
      ...prev,
      [field]: formattedDate,
    };
    
    return updated;
  });
};

const handleReset = () => {
  const resetData: Record<string, any> = {};
  formConfig?.sections?.forEach((section) => {
    section.widgets.forEach((widget) => {
      const fieldName = widget.mappedApiField;
      resetData[fieldName] =
        initialData?.[fieldName] ??
        // initialData?.extendedAttributes?.[fieldName] ??
        // initialData?.extendedAttributes?.['shipTo']?.[fieldName] ??
        (widget.widgetType === 'checkbox' || widget.widgetType === 'multiselect-dropdown' ? [] : '');
    });
  });
  setFormData(resetData);
  setErrors({}); 
};

const renderField = (widget: Widget) => {
  const fieldName = widget.mappedApiField;
  const fieldValue = formData[fieldName] || (widget.widgetType === 'checkbox' || widget.widgetType === 'multiselect-dropdown' ? [] : '');
  const error = errors[fieldName];
  
  // Determine if field should be editable: if it's a new record, make all fields editable
  const isFieldEditable = isNewRecord || widget.config.editable;

  return (
    <Box
      key={widget.id}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        mb: 3,
        gap: 2,
      }}
    >
      <Typography
        sx={{
          minWidth: '140px',
          width: '140px',
          fontWeight: 400,
          fontSize: '14px',
          color: '#666',
          lineHeight: '35px',
          textAlign: 'left',
          flexShrink: 0,
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {widget.config.label || ""}
        {widget.config.required && (
          <Typography component="span" sx={{ color: '#f44336', ml: 0.5 }}>
            *
          </Typography>
        )}
      </Typography>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {widget.widgetType === 'text-input' ? (
          <>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={fieldValue}
              onChange={handleInputChange(fieldName)}
              type={widget.config.inputType?.selected || 'text'}
              disabled={!isFieldEditable}
              error={!!error}
              helperText={error}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  fontSize: '14px',
                  backgroundColor: isFieldEditable ? '#fff' : '#f5f5f5',
                  '& fieldset': {
                    borderColor: error ? '#f44336' : '#e0e0e0',
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: error ? '#f44336' : '#c4c4c4',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: error ? '#f44336' : '#1976d2',
                    borderWidth: '1px',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                  color: '#333',
                  padding: '10px 12px',
                },
              }}
            />
          </>
        ) : widget.widgetType === 'dropdown' ? (
          <FormControl fullWidth error={!!error}>
            <Select
              value={fieldValue}
              disabled={!isFieldEditable}
              onChange={handleSelectChange(fieldName)}
              sx={{
                height: '40px',
                fontSize: '14px',
                color: '#333',
                backgroundColor: isFieldEditable ? '#fff' : '#f5f5f5',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: error ? '#f44336' : '#e0e0e0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: error ? '#f44336' : '#c4c4c4',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: error ? '#f44336' : '#1976d2',
                  borderWidth: '1px',
                },
                '& .MuiSelect-select': {
                  padding: '10px 12px',
                },
              }}
            >
              <MenuItem value="" sx={{ fontSize: '14px', color: '#999' }}>
                {widget.config.hintText || 'Select an option'}
              </MenuItem>
              {(options[widget.mappedApiField]?.length ? options[widget.mappedApiField] : (widget.config.list ?? [])).map((option) => (
                <MenuItem key={option} value={option} sx={{ fontSize: '14px' }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        ) : widget.widgetType === 'checkbox' ? (
          (options[widget.mappedApiField]?.length ? options[widget.mappedApiField] : (widget.config.list ?? [])).length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(options[widget.mappedApiField]?.length ? options[widget.mappedApiField] : (widget.config.list ?? [])).map((item, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={Array.isArray(formData[fieldName]) ? formData[fieldName].includes(item) : false}
                      onChange={(e) => {
                        const currentValues = Array.isArray(formData[fieldName]) ? [...formData[fieldName]] : [];
                        if (e.target.checked) {
                          currentValues.push(item);
                        } else {
                          const itemIndex = currentValues.indexOf(item);
                          if (itemIndex >= 0) {
                            currentValues.splice(itemIndex, 1);
                          }
                        }
                        setErrors((prev) => ({ ...prev, [fieldName]: '' }));
                        setFormData((prev) => ({
                          ...prev,
                          [fieldName]: currentValues,
                        }));
                      }}
                      disabled={!isFieldEditable}
                      size="small"
                    />
                  }
                  label={item}
                  sx={{
                    fontSize: '14px',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px',
                    },
                  }}
                />
              ))}
              {error && <FormHelperText error>{error}</FormHelperText>}
            </Box>
          ) : (
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData[fieldName])}
                  onChange={(e) => {
                    setErrors((prev) => ({ ...prev, [fieldName]: '' }));
                    setFormData((prev) => ({
                      ...prev,
                      [fieldName]: e.target.checked,
                    }));
                  }}
                  disabled={!isFieldEditable}
                  size="small"
                />
              }
              label={widget.config.hintText || ''}
              sx={{
                fontSize: '14px',
                '& .MuiFormControlLabel-label': {
                  fontSize: '14px',
                },
              }}
            />
          )
        ) : widget.widgetType === 'radio-group' ? (
          <FormControl error={!!error}>
            <RadioGroup
              row
              value={fieldValue || ''}
              onChange={(e) => {
                setErrors((prev) => ({ ...prev, [fieldName]: '' }));
                setFormData((prev) => ({
                  ...prev,
                  [fieldName]: e.target.value,
                }));
              }}
            >
              {(options[widget.mappedApiField]?.length ? options[widget.mappedApiField] : (widget.config.list ?? [])).map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio color="primary" size="small" disabled={!isFieldEditable} />}
                  label={option}
                  sx={{
                    fontSize: '14px',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px',
                    },
                  }}
                />
              ))}
            </RadioGroup>
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        ) : widget.widgetType === 'ranged-date' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Box sx={{ display: 'flex', gap: '12px' }}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={fieldValue?.startDate || ''}
                onChange={(e) => {
                  const updated = { ...fieldValue, startDate: e.target.value };
                  setErrors((prev) => ({ ...prev, [fieldName]: '' }));
                  setFormData((prev) => ({
                    ...prev,
                    [fieldName]: updated,
                  }));
                }}
                disabled={!isFieldEditable}
                error={!!error}
                helperText={error}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={fieldValue?.endDate || ''}
                onChange={(e) => {
                  const updated = { ...fieldValue, endDate: e.target.value };
                  setErrors((prev) => ({ ...prev, [fieldName]: '' }));
                  setFormData((prev) => ({
                    ...prev,
                    [fieldName]: updated,
                  }));
                }}
                disabled={!isFieldEditable}
                error={!!error}
              />
            </Box>
            {widget.config.difference && fieldValue?.startDate && fieldValue?.endDate && (
              <TextField
                label="Difference"
                size="small"
                value={() => {
                  const start = new Date(fieldValue.startDate);
                  const end = new Date(fieldValue.endDate);
                  const diffTime = end.getTime() - start.getTime();
                  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                  return `${diffDays} day(s)`;
                }}
                InputProps={{ readOnly: true }}
              />
            )}
          </Box>
        ) : widget.widgetType === 'textarea' ? (
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            size="small"
            value={fieldValue || ''}
            onChange={handleInputChange(fieldName)}
            placeholder={widget.config.placeholder}
            disabled={!isFieldEditable}
            error={!!error}
            helperText={error}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                backgroundColor: isFieldEditable ? '#fff' : '#f5f5f5',
                '& fieldset': {
                  borderColor: error ? '#f44336' : '#e0e0e0',
                  borderWidth: '1.5px',
                },
                '&:hover fieldset': {
                  borderColor: error ? '#f44336' : '#c4c4c4',
                },
                '&.Mui-focused fieldset': {
                  borderColor: error ? '#f44336' : '#1976d2',
                  borderWidth: '1px',
                },
              },
            }}
          />
        ) : widget.widgetType === 'date-picker' ? (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            label={widget.config.label || 'Date'}
            value={fieldValue || ''}
            onChange={handleDateChange(fieldName)}
            renderInput={(params) => (
              <TextField {...params} error={!!error} helperText={error} />
            )}
          />
</LocalizationProvider>
        ) : widget.widgetType === 'slider' ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Slider
              value={fieldValue || widget.config.min || 0}
              min={Number(widget.config.min) || 0}
              max={Number(widget.config.max) || 100}
              step={Number(widget.config.step) || 1}
              onChange={(_, newValue) => {
                setErrors((prev) => ({ ...prev, [fieldName]: '' }));
                setFormData((prev) => ({
                  ...prev,
                  [fieldName]: newValue,
                }));
              }}
              valueLabelDisplay="auto"
              disabled={!isFieldEditable}
              size="small"
            />
            {error && <FormHelperText error>{error}</FormHelperText>}
          </Box>
        ) : widget.widgetType === 'multiselect-dropdown' ? (
          <FormControl fullWidth size="small" error={!!error}>
            <InputLabel>{widget.config.hintText || 'Select options'}</InputLabel>
            <Select
              multiple
              value={Array.isArray(fieldValue) ? fieldValue : []}
              onChange={(e) => {
                const selectedValues = e.target.value as string[];
                if (selectedValues.length <= (widget.config.maxOptions || 10)) {
                  setErrors((prev) => ({ ...prev, [fieldName]: '' }));
                  setFormData((prev) => ({
                    ...prev,
                    [fieldName]: selectedValues,
                  }));
                }
              }}
              renderValue={(selected) => (selected as string[]).join(', ')}
              disabled={!isFieldEditable}
              sx={{
                backgroundColor: isFieldEditable ? '#fff' : '#f5f5f5',
              }}
            >
              {(options[widget.mappedApiField]?.length ? options[widget.mappedApiField] : (widget.config.list ?? [])).map((option, index) => (
                <MenuItem key={index} value={option} sx={{ fontSize: '14px' }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        ) : widget.widgetType === 'camera' ? (
          <Box sx={{ mt: 1, position: 'relative' }}>
          <GenericFileUpload
            label={widget.config.label || 'Upload Image'}
            resolution={{ width: 240, height: 240 }} 
            defaultFile={fieldValue?.url || ''}
            setDefaultFile={(value: string | ((prev: string) => string)) => {
              setFormData((prev) => ({
                ...prev,
                [fieldName]: {
                  ...prev[fieldName],
                  url: typeof value === 'function' ? value(prev[fieldName]?.url || '') : value || '',
                },
              }));
              setErrors((prev) => ({ ...prev, [fieldName]: '' }));
            }}
            accept={{ image: ['*'] }}
            fileAttributes={{
              fileName: fieldValue?.fileName || '',
              fileType: fieldValue?.fileType || '',
            }}
            setFileAttributes={(value: fileAttributes | ((prev: fileAttributes) => fileAttributes)) => {
              setFormData((prev) => ({
                ...prev,
                [fieldName]: {
                  ...prev[fieldName],
                },
              }));
            }}
            fetchBlobKey={true}
          />
          {error && <FormHelperText error>{error}</FormHelperText>}
        </Box>
        ) : widget.widgetType === 'location' ? (
          widget.config.display ? (
            <Box>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '12px', mb: 1, display: 'block' }}>
                Select Location
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter location"
                value={fieldValue?.location || ''}
                onChange={(e) => {
                  const updated = { ...fieldValue, location: e.target.value };
                  setErrors((prev) => ({ ...prev, [fieldName]: '' }));
                  setFormData((prev) => ({
                    ...prev,
                    [fieldName]: updated,
                  }));
                }}
                disabled={!isFieldEditable}
                sx={{ mb: 1 }}
                error={!!error}
                helperText={error}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  label="Latitude"
                  type="number"
                  value={fieldValue?.lat || ''}
                  onChange={(e) => {
                    const updated = { ...fieldValue, lat: e.target.value };
                    setErrors((prev) => ({ ...prev, [fieldName]: '' }));
                    setFormData((prev) => ({
                      ...prev,
                      [fieldName]: updated,
                    }));
                  }}
                  disabled={!isFieldEditable}
                  error={!!error}
                />
                <TextField
                  size="small"
                  label="Longitude"
                  type="number"
                  value={fieldValue?.lng || ''}
                  onChange={(e) => {
                    const updated = { ...fieldValue, lng: e.target.value };
                    setErrors((prev) => ({ ...prev, [fieldName]: '' }));
                    setFormData((prev) => ({
                      ...prev,
                      [fieldName]: updated,
                    }));
                  }}
                  disabled={!isFieldEditable}
                  error={!!error}
                />
              </Box>
            </Box>
          ) : null
        ) : (
          <Typography sx={{ color: '#999', fontSize: '14px', lineHeight: '40px' }}>
            Unsupported widget type: {widget.widgetType}
          </Typography>
        )}
      </Box>
    </Box>
  );
};


  // Define types for master data
  type MasterItem = {
    id: string;
    name: string;
    contact: string;
    distributor: string;
  };

  type MasterDataType = {
    [key: string]: MasterItem[];
  };

  // Add state for dropdown visibility and selected master
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [selectedMaster, setSelectedMaster] = React.useState('SalesRep');
  const [selectedMasterTab, setSelectedMasterTab] = React.useState(0);

  const masters = ['SalesRep', 'Outlet', 'Product', 'Route'] as const;
  type MasterKey = typeof masters[number];

  if (!formConfig?.sections?.length) {
    console.error("No sections found in formConfig");
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography color="error">Error: Invalid form configuration</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Page Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onBack && (
            <IconButton onClick={onBack} size="small">
              <ArrowBack sx={{ fontSize: 20, color: '#333' }} />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
            {mdmState.name} {isNewRecord ? '(New Record)' : '(Edit Record)'}
          </Typography>
        </Box>
        {/* <TextField
          placeholder="Search"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: 250,
            '& .MuiOutlinedInput-root': {
              height: '36px',
              fontSize: '14px',
              backgroundColor: '#fff'
            },
            '& .MuiInputBase-input': { padding: '8px 12px' },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  sx={{
                    p: 0.6,
                    backgroundColor: '#237C9D',
                    borderRadius: '50%',
                    '&:hover': {
                      backgroundColor: '#1e6b7d'
                    }
                  }}
                >
                  <Search sx={{ fontSize: 18, color: '#fff' }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        /> */}
      </Paper>

      {/* Main Content Container */}
      <Container 
        maxWidth="xl" 
        sx={{ 
          flex: 1, 
          py: 0, 
          position: 'relative', 
          px: 0, 
          padding: '0',
          '&.MuiContainer-root': { 
        paddingLeft: 0,
        paddingRight: 0
          }
        }}
      >
        <Paper
          elevation={0}
          sx={{
        backgroundColor: '#fff',
        borderRadius: 0,
        minHeight: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
          }}
        >
          {/* Dropdown Content */}
          {isDropdownOpen && (
        <Box sx={{
          position: 'absolute',
          bottom: '80px',
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '8px 8px 0 0',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
          zIndex: 20,
          maxHeight: '400px',
          display: 'flex',
          flexDirection: 'column'
        }}>
        {/* Dropdown Header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Typography sx={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
            Linked Masters
          </Typography>
          <IconButton 
            onClick={() => setIsDropdownOpen(false)}
            size="small"
            sx={{ color: '#666' }}
          >
            <KeyboardArrowUp />
          </IconButton>
            </Box>


            {/* Master Data Table */}
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto', 
              maxHeight: '250px',
              backgroundColor: '#fff',
              border: '1px solid #4dd0e1',
              borderTop: 'none'
            }}>
            </Box>
          </Box>
          )}

          {/* Form Tabs */}
          <Box sx={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                px: 3,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#666',
                  padding: '12px 20px',
                  minHeight: '48px',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                },
                '& .Mui-selected': {
                  color: '#24c6b1 !important',
                  fontWeight: 500,
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#24c6b1',
                  height: '2px',
                },
                '& .MuiTabs-scrollButtons': {
                  color: '#666',
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },
                },
              }}
            >
              {formConfig.sections.map((section, index) => (
                <Tab key={section.id} label={section.name} />
              ))}
            </Tabs>
          </Box>
              {/* Info Message */}
              <Box sx={{ 
                p: 3, 
                pb: 0, 
                display: 'flex', 
                justifyContent: 'flex-end' 
              }}>
                <Typography sx={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  fontStyle: 'italic',
                  backgroundColor: '#f5f5f5',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0'
                }}>
                  Fields that are greyed out on this screen are not editable
                </Typography>
              </Box>
          {/* Form Content */}
          <Box sx={{ flex: 1, p: 3, pb: '100px' }}>
            {formConfig.sections.map((section, index) => (
              <TabPanel key={section.id} value={tabValue} index={index}>
                <Grid container spacing={0}>
                  {section.widgets.filter(widget => !!widget.mappedApiField).map(widget => (
                    <Grid item xs={12} md={6} key={widget.id} sx={{ pr: { md: 4 } }}>
                      {renderField(widget)}
                    </Grid>
                  ))}
                </Grid>
              </TabPanel>
            ))}
          </Box>
          </Paper>

          {/* Sticky Footer with Action Buttons */}
          <Box sx={{
            position: 'sticky',
            bottom: 0,
            p: 1.5,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
            zIndex: 10,
            overflow: 'hidden',
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}>
              <Button
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#666',
                  borderColor: '#adb5bd',
                  borderRadius: '4px',
                  borderWidth: '1.5px',
                  minWidth: '80px',
                  '&:hover': {
                    borderColor: '#c4c4c4',
                    backgroundColor: '#f9f9f9',
                  },
                }}
                onClick={handleReset}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                sx={{
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  fontSize: '14px',
                  backgroundColor: '#24c6b1',
                  borderRadius: '4px',
                  minWidth: '80px',
                  '&:hover': {
                    backgroundColor: '#24c6b1',
                  },
                }}
                onClick={handleSave}
              >
                Save
              </Button>
            </Box>
            </Box>
      </Container>
    </Box>
  );
};